/** **AutoLISP's READER — text to forms, and nothing else.**
 *
 * ⚠⚠ Kept apart from the evaluator on purpose. Every dialect argument this app will ever have —
 * whether `1+` is a symbol, whether `'(a b)` quotes, what `1.` means — is settled HERE, once,
 * where it can be tested without a drawing, a command or a browser. `qa/lisp_check.py` reads
 * this file's answers directly.
 */

/** A LISP value. ⚠ `null` IS `nil`: AutoLISP has one falsy value and conflating it with
 *  JavaScript's `undefined` is how a missing argument starts meaning "the empty list". */
export type Lv = number | string | Sym | Lv[] | Fn | null;

/** A symbol. ⚠ A CLASS, not a bare string: `"LINE"` the string and `LINE` the symbol are
 *  different things in this language, and `(command "_.LINE" …)` passes the first. */
export class Sym {
  constructor(readonly name: string) {}
  toString() { return this.name; }
}

/** A callable — a builtin or a `defun`. ⚠ ASYNC, all of them, because `(command …)` has to wait
 *  for the drawing to change and a caller cannot know which functions reach it. */
export interface Fn {
  lisp: true;
  name: string;
  call: (args: Lv[]) => Promise<Lv>;
}

const sym = (n: string) => new Sym(n.toUpperCase());

/** ⚠⚠ **SYMBOLS ARE UPPER-CASED AND STRINGS ARE NOT.** AutoLISP is case-insensitive for names —
 *  `(SetQ x 1)` and `(setq X 1)` are the same — and case-SENSITIVE for text, because a text
 *  string is drawing content. Folding both ways has broken this in other implementations. */
export function readAll(src: string): Lv[] {
  const out: Lv[] = [];
  let i = 0;

  const ws = () => {
    for (;;) {
      while (i < src.length && /\s/.test(src[i])) i++;
      // ⚠ `;` to end of line — AutoLISP's only comment, and the reference loops are full of them.
      if (src[i] === ";") { while (i < src.length && src[i] !== "\n") i++; continue; }
      break;
    }
  };

  const form = (): Lv => {
    ws();
    if (i >= src.length) throw new Error("unexpected end of input");
    const c = src[i];
    if (c === "(") { i++; return list(); }
    if (c === ")") throw new Error("misplaced )");
    // ⚠ `'x` is `(quote x)`. The reference files do not use it; every LISP a drafter pastes in
    // does.
    if (c === "'") { i++; return [sym("quote"), form()]; }
    if (c === '"') { i++; return str(); }
    return atom();
  };

  const list = (): Lv[] => {
    const items: Lv[] = [];
    for (;;) {
      ws();
      if (i >= src.length) throw new Error("unclosed (");
      if (src[i] === ")") { i++; return items; }
      items.push(form());
    }
  };

  const str = (): string => {
    let s = "";
    while (i < src.length && src[i] !== '"') {
      if (src[i] === "\\" && i + 1 < src.length) {
        // ⚠ AutoLISP's escapes, not JavaScript's: `\\n` `\\t` `\\\\` `\\"` and nothing else.
        const n = src[++i];
        s += n === "n" ? "\n" : n === "t" ? "\t" : n;
        i++;
        continue;
      }
      s += src[i++];
    }
    if (src[i] !== '"') throw new Error("unclosed string");
    i++;
    return s;
  };

  const atom = (): Lv => {
    const start = i;
    while (i < src.length && !/[\s()";]/.test(src[i])) i++;
    const t = src.slice(start, i);
    // ⚠⚠ **`1+` AND `1-` ARE FUNCTION NAMES, NOT NUMBERS.** `Number("1+")` is NaN, which is the
    // easy way to get this right by accident and the easy way to get `-` wrong: `Number("-")` is
    // also NaN, and `-` IS a function. The test is the whole token against a number pattern.
    if (/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(t)) return Number(t);
    if (t.toUpperCase() === "NIL") return null;
    return sym(t);
  };

  for (;;) {
    ws();
    if (i >= src.length) break;
    out.push(form());
  }
  return out;
}
